import React, { useState } from 'react';
import { Card, ListGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { IconCircle, IconEdit } from '../../assets/icons';
import { TripState } from '../../types/trip.store.type';
import { Reservation } from 'guest-data-types-ts-lib';
import { TranslationUtils } from '../../utils';
import { ActionInviteGuestModal, InviteGuestModal } from '..';
import './InviteGuestPanel.scss';

interface InviteGuestPanelProps {
    reservation: Reservation;
    isInvitesFull: boolean;
}

interface InviteGuestItem {
    id: number;
    guestId: number;
    firstName: string;
    lastName: string;
    icon: any;
    invited: any;
    email: any;
}

export const InviteGuestPanel: React.FC<InviteGuestPanelProps> = (props) => {
    const { reservation, isInvitesFull } = props;
    const daysBefore = 7;
    const emptyGuestItem: InviteGuestItem = {
        id: 1,
        guestId: 1,
        icon: '',
        invited: '',
        firstName: '',
        lastName: '',
        email: '',
    };
    const [isShowingInviteGuestModal, setIsShowingInviteGuestModal] = useState(false);
    const [isShowingAddGuestModal, setIsShowingAddGuestModal] = useState(false);
    const [modalAddItem, setModalAddItem] = useState<InviteGuestItem>(emptyGuestItem);
    const [modalItem, setModalItem] = useState<InviteGuestItem>(emptyGuestItem);
    const trip = useSelector((store: { trip: TripState }) => store.trip);
    const { guests, unit } = trip;

    if (!guests || !unit) {
        return null;
    }

    const { country_code } = unit;

    const toggleInviteGuestModal = (item: InviteGuestItem) => {
        setModalItem(item);
        setIsShowingInviteGuestModal(() => !isShowingInviteGuestModal);
    };

    const handleInviteGuestClose = () => {
        setIsShowingInviteGuestModal(() => !isShowingInviteGuestModal);
    };

    const toggleAddGuestModal = (item: InviteGuestItem) => {
        setModalAddItem(item);
        setIsShowingAddGuestModal(() => !isShowingAddGuestModal);
    };

    const handleAddGuestModalClose = () => {
        setIsShowingAddGuestModal(() => !isShowingAddGuestModal);
    };

    const { primary, secondary } = guests;

    const getPrimaryInvitee = (): InviteGuestItem => {
        return {
            id: 0,
            guestId: 0,
            icon: <IconCircle className="icon-invite-guest" />,
            firstName: primary.first_name || '',
            lastName: primary.last_name || '',
            invited: `${primary.first_name} ${primary.last_name}`,
            email: primary.email,
        };
    };

    const mappedSecondaryInvitees = secondary.map(
        (invite: any, i: number) =>
            ({
                id: i + 1,
                icon: '<IconCircle className="icon-invite-guest" />',
                guestId: invite.guest_id,
                firstName: invite.first_name,
                lastName: invite.last_name,
                invited: `${invite.first_name} ${invite.last_name}`,
                email: invite.email,
            } as InviteGuestItem)
    );

    const primaryGuest = getPrimaryInvitee();

    return (
        <div className="invite-guest-panel">
            <div className="interline-invite-guest" />
            <span tabIndex={0} className="body-bold-l title-invite-guest">
                {TranslationUtils.get('who_is_coming')}
            </span>
            <div className="interline-invite-guest-note" />
            <span tabIndex={0} className="body-regular">
                {['US', 'CA'].includes(country_code)
                    ? TranslationUtils.get('invite_guest_note_same_day')
                    : TranslationUtils.get('invite_guest_note', { daysBefore })}
            </span>
            <div className="interline-invite-guest" />
            <div tabIndex={0} className="item-guest">
                <div className="block-guest">
                    <span className="body-bold title-guest ">{TranslationUtils.get('name')}</span>
                </div>
                <div className="block-guest">
                    <span className="body-bold title-guest">{TranslationUtils.get('email_addresses_guests')}</span>
                </div>
                <div className="item-next-guest" />
            </div>
            <Card>
                <ListGroup variant="flush">
                    {
                        <ListGroup.Item>
                            <div tabIndex={0} className="item-guest">
                                <div className="circle-info-guest d-none d-md-block">
                                    <span>{primaryGuest.invited.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="category-guest">
                                    <span className="body-regular title-guest text-guest-overflow">{primaryGuest.invited}</span>
                                </div>
                                <div className="category-guest">
                                    <span className="body-regular title-guest text-guest-overflow">{primaryGuest.email}</span>
                                </div>
                                <div className="item-next-guest" />
                            </div>
                        </ListGroup.Item>
                    }
                    {mappedSecondaryInvitees.map((item, i) => (
                        <ListGroup.Item key={i}>
                            <div className="item-guest">
                                <div className="circle-info-guest d-none d-md-block">
                                    <span>{item.invited.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="category-guest">
                                    <span id={`span-edit-inviteGuestPanel-${i}`} className="body-regular title-guest text-guest-overflow">
                                        {item.invited}
                                    </span>
                                </div>
                                <div className="category-guest">
                                    <span className="body-regular title-guest text-guest-overflow">{item.email}</span>
                                </div>
                                <div className="item-next-guest">
                                    <a
                                        tabIndex={0}
                                        role="button"
                                        id={`link-edit-inviteGuestPanel-${i}`}
                                        title={`${TranslationUtils.get('edit_guest')} ${item.invited}`}
                                        onClick={() => toggleInviteGuestModal(item)}
                                        onKeyPress={() => toggleInviteGuestModal(item)}
                                    >
                                        <IconEdit className="icon-invite-guest pointer" />
                                    </a>
                                </div>
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Card>
            <div className="interline-invite-guest-note" />

            <div className="col-xs-6 text-right">
                {!isInvitesFull && (
                    <a
                        tabIndex={0}
                        role="button"
                        id="link-add-invite-guest-panel"
                        className="body-regular button-add-invite"
                        onClick={() => toggleAddGuestModal(emptyGuestItem)}
                        onKeyPress={() => toggleAddGuestModal(emptyGuestItem)}
                    >
                        + {TranslationUtils.get('add_guest')}
                    </a>
                )}
            </div>
            <div className="interline-invite-guest" />
            <InviteGuestModal
                isInvitesFull={isInvitesFull}
                reservation={reservation}
                item={modalAddItem}
                show={isShowingAddGuestModal}
                handleClose={handleAddGuestModalClose}
                currentGuestCount={mappedSecondaryInvitees.length}
            />
            <ActionInviteGuestModal
                item={modalItem}
                show={isShowingInviteGuestModal}
                handleClose={handleInviteGuestClose}
                currentGuestCount={mappedSecondaryInvitees.length}
                reservationId={reservation.id}
            />
        </div>
    );
};
